import React, { useState } from 'react';
import { KeyRound, LogOut, UserCheck } from 'lucide-react';
import { useAccess } from '../../contexts/AccessContext';

/** Persistent "unlock" pill for guests; account chip for signed-in users.
 *  `variant="dark"` is tuned for the navy sidebar, "light" for cream pages. */
export const AccessPill = ({ variant = 'light', compact = false, className = '' }) => {
  const { loading, isGuest, email, name, picture, openPrompt, signOut, demoted } = useAccess();
  const [menu, setMenu] = useState(false);
  if (loading) return null;

  if (isGuest && !email) {
    return (
      <button
        type="button"
        onClick={openPrompt}
        className={`group inline-flex items-center gap-1.5 ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-3.5 py-1.5 text-xs'} rounded-full font-semibold text-white bg-gradient-to-b from-[#c9a437] to-[#8c691b] hover:from-[#e5c976] hover:to-[#9a7523] shadow-[0_6px_16px_-6px_rgba(154,123,36,0.8)] border border-[#e5c976]/50 transition-all whitespace-nowrap ${className}`}
        data-testid="access-unlock-pill"
        aria-label="Sign in with your email to unlock the full library"
      >
        <KeyRound className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform" />
        {compact ? 'Full access' : 'Get full access'}
      </button>
    );
  }

  const dark = variant === 'dark';
  const label = name || email;
  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setMenu((m) => !m)}
        className={`inline-flex items-center gap-2 ${compact ? 'px-2 py-1' : 'px-2.5 py-1.5'} rounded-full text-xs font-medium border transition-colors ${dark ? 'bg-white/5 border-white/15 text-[#cfd7ea] hover:bg-white/10' : 'bg-white border-[#e1ddd6] text-[#0f1729] hover:border-[#9a7b24]'}`}
        data-testid="access-account-chip"
        title={email}
      >
        {picture ? (
          <img src={picture} alt="" className="w-5 h-5 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#c9a437] to-[#8c691b] flex items-center justify-center">
            <UserCheck className="w-3 h-3 text-white" />
          </span>
        )}
        <span className="max-w-[9rem] truncate" data-testid="access-account-label">{label}</span>
        {demoted && <span className="text-[9px] uppercase tracking-wider text-amber-500">open only</span>}
      </button>
      {menu && (
        <div
          className={`absolute z-50 mt-1.5 ${dark ? 'left-0' : 'right-0'} min-w-[14rem] rounded-xl border shadow-xl p-2 ${dark ? 'bg-[#0f1729] border-white/15 text-[#cfd7ea]' : 'bg-white border-[#e1ddd6] text-[#0f1729]'}`}
          data-testid="access-account-menu"
        >
          <p className="px-2 pt-1 text-[10px] uppercase tracking-wider opacity-60">Signed in as</p>
          <p className="px-2 pb-2 text-xs font-semibold truncate">{email}</p>
          <button
            type="button"
            onClick={signOut}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${dark ? 'hover:bg-white/10' : 'hover:bg-[#f1ece1]'}`}
            data-testid="access-signout-button"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out on this device
          </button>
        </div>
      )}
    </div>
  );
};

export default AccessPill;
